export type TScheduledAgentFacet = 'all' | 'mine' | 'workspace';

export enum ScheduleFrequency {
  HOURLY = 'HOURLY',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export interface ISimpleSchedule {
  type: 'SIMPLE';
  frequency: ScheduleFrequency;
  minute: number;
  hour?: number;
  daysOffset?: number;
  weekOffset?: number;
  timezone: string;
}

export const AgentNotificationChannel = {
  EMAIL: 'EMAIL',
  SLACK: 'SLACK',
  TEAMS: 'TEAMS',
} as const;

export type TAgentNotificationChannel =
  (typeof AgentNotificationChannel)[keyof typeof AgentNotificationChannel];

export interface IAgentNotificationTarget {
  id: string;
  name: string;
}

export type TAgentNotificationPauseCode = 'APP_DISCONNECTED' | 'TARGET_UNREACHABLE';

export interface IAgentNotification {
  channel: TAgentNotificationChannel;
  target?: IAgentNotificationTarget;
  pausedAt?: string;
  pausedReason?: string;
  pausedCode?: TAgentNotificationPauseCode;
}

export type TChannelUnavailableReason = 'NOT_CONFIGURED' | 'NOT_CONNECTED';

export interface IAgentNotificationChannelOption {
  channel: TAgentNotificationChannel;
  label: string;
  requiresTarget: boolean;
  available: boolean;
  toolkitSlug: string | null;
  toolkitName: string | null;
  logoUrl: string | null;
  unavailableReason: TChannelUnavailableReason | null;
}

export interface IAgentNotificationChannelsResponse {
  channels: IAgentNotificationChannelOption[];
}

export interface IAgentNotificationTargetsResponse {
  targets: IAgentNotificationTarget[];
}

export const ScheduledAgentInstallationScope = {
  WORKSPACE: 'WORKSPACE',
  PERSONAL: 'PERSONAL',
} as const;

export type TScheduledAgentInstallationScope =
  (typeof ScheduledAgentInstallationScope)[keyof typeof ScheduledAgentInstallationScope];

export const ScheduledAgentInstallationScopeLabel = {
  WORKSPACE: 'Workspace',
  PERSONAL: 'Only me',
} as const satisfies Record<TScheduledAgentInstallationScope, string>;

export const ScheduledAgentSubscriptionDefault = {
  EVERYONE: 'EVERYONE',
  SPECIFIC_PEOPLE: 'SPECIFIC_PEOPLE',
} as const;

export type TScheduledAgentSubscriptionDefault =
  (typeof ScheduledAgentSubscriptionDefault)[keyof typeof ScheduledAgentSubscriptionDefault];

export const ScheduledAgentManager = {
  USER: 'USER',
  COMMENDA: 'COMMENDA',
} as const;

export type TScheduledAgentManager =
  (typeof ScheduledAgentManager)[keyof typeof ScheduledAgentManager];

export type TScheduledAgentRunTriggerSource = 'RECURRENCE' | 'MANUAL';

export interface IMySubscription {
  subscribed: boolean;
  emailOptedOut: boolean;
}

export interface IScheduledAgent {
  id: string;
  companyId: number;
  title: string;
  prompt: string;
  modelId: string;
  installationScope: TScheduledAgentInstallationScope;
  subscriptionDefault: TScheduledAgentSubscriptionDefault;
  managedBy: TScheduledAgentManager;
  managedAgentId: string | null;
  schedule: ISimpleSchedule | null;
  notifications: IAgentNotification[];
  scopeConfig: { entityIds?: number[] } | null;
  enabled: boolean;
  pausedReason: string | null;
  consecutiveFailures: number;
  createdByUserId: string;
  createdByName: string | null;
  isEditableByMe: boolean;
  isOperableByMe: boolean;
  isDeletableByMe: boolean;
  mySubscription: IMySubscription;
  subscriberCount: number;
  subscriberUserIds: string[];
  optedOutUserIds: string[];
  lastRunAt: string | null;
  nextRunAt: string | null;
  unreadRunCount: number;
  isRunning: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IScheduledAgentRunSummary {
  taskId: number;
  runNumber: number;
  title: string | null;
  status: string;
  triggerSource: TScheduledAgentRunTriggerSource;
  createdAt: string;
  completedAt: string | null;
  skipReason: string | null;
  conversationId: string | null;
  agentRunId: string | null;
  isUnreadByMe: boolean;
  totalCostUsd: number | null;
  totalTokens: number | null;
}

export interface IScheduledAgentRunsPage {
  runs: IScheduledAgentRunSummary[];
  nextCursor: number | null;
}

export interface IManagedAgentSummary {
  key: string;
  title: string;
  description: string;
  prompt: string;
  schedule: Omit<ISimpleSchedule, 'type' | 'minute' | 'timezone'> & {
    type?: 'SIMPLE';
  };
  installationScope: TScheduledAgentInstallationScope;
}

export interface IScheduledAgentListResponse {
  agents: IScheduledAgent[];
}

export interface IScheduledAgentDetailResponse {
  agent: IScheduledAgent;
  runs: IScheduledAgentRunSummary[];
}

export interface CreateScheduledAgentParams {
  companyId: number;
  title: string;
  prompt: string;
  schedule: ISimpleSchedule;
  modelId?: string;
  installationScope?: TScheduledAgentInstallationScope;
  subscriptionDefault?: TScheduledAgentSubscriptionDefault;
  subscriberUserIds?: string[];
  optedOutUserIds?: string[];
  notifications?: IAgentNotification[];
  scopeConfig?: { entityIds?: number[] };
  enabled?: boolean;
}

export interface UpdateScheduledAgentParams {
  title?: string;
  prompt?: string;
  schedule?: ISimpleSchedule;
  modelId?: string;
  installationScope?: TScheduledAgentInstallationScope;
  subscriptionDefault?: TScheduledAgentSubscriptionDefault;
  subscriberUserIds?: string[];
  optedOutUserIds?: string[];
  notifications?: IAgentNotification[];
  scopeConfig?: { entityIds?: number[] };
}

export interface UpdateMySubscriptionParams {
  subscribed: boolean;
  emailOptedOut?: boolean;
}
